import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";

const HERO_IMG = "https://images.unsplash.com/photo-1593696140826-c58b021acf8b?crop=entropy&cs=srgb&fm=jpg&q=85";
const TEX_WOOD = "https://images.unsplash.com/photo-1506689205310-0a29c388691c?crop=entropy&cs=srgb&fm=jpg&q=85";
const TEX_MARBLE = "https://images.unsplash.com/photo-1579194355380-e0d9ab53db5a?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function LandingPage() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <PublicNav light />

      {/* Hero */}
      <section className="relative" style={{ height: "100vh", minHeight: 660, background: "#1C1C1C" }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-65"/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,28,28,0.3) 0%, rgba(28,28,28,0.85) 100%)" }}/>
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20 text-white">
          <div className="overline" style={{ color: "#A68A64" }}>Sunshine Coast · Queensland</div>
          <h1 className="font-serif font-light mt-5 max-w-4xl" style={{ fontSize: "clamp(46px, 8vw, 92px)", lineHeight: 1.02, letterSpacing: "-0.01em" }}>
            Entertainment, <em className="not-italic italic" style={{ color: "#A68A64" }}>designed into your home.</em>
          </h1>
          <p className="mt-7 text-lg text-white/75 max-w-2xl">
            Luxe Media Walls creates bespoke architectural entertainment spaces combining technology, cabinetry, lighting and premium materials into one beautifully resolved design.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/enquiry" data-testid="btn-hero-start" className="btn-accent" style={{ padding: "16px 28px" }}>
              Start Your Project <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
            </Link>
            <Link to="/designs" data-testid="btn-hero-explore" className="btn-outline" style={{ borderColor: "rgba(255,255,255,0.4)", color: "white", padding: "15px 28px" }}>
              Explore Designs
            </Link>
          </div>
          <div className="mt-6 text-xs text-white/50 tracking-widest uppercase">Bespoke projects typically from $20,000</div>
        </div>
      </section>

      {/* Value */}
      <section className="bg-[color:hsl(var(--background))] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[420px,1fr] gap-16">
          <div>
            <div className="overline">01 · The Approach</div>
            <h2 className="font-serif font-light mt-4" style={{ fontSize: "56px", lineHeight: 1.05 }}>
              A studio built around one thing — walls that live beautifully.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              We work with homeowners, architects and interior designers across the Sunshine Coast to compose entertainment walls of restraint and craft — stone, timber, plaster and light, resolved into a single considered gesture.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-px bg-border border border-border">
            {[
              { t: "Bespoke Design", d: "Every wall composed from scratch to your architecture, brief and life." },
              { t: "Curated Materials", d: "Trade relationships with premium stone, timber and joinery suppliers." },
              { t: "Full AV Integration", d: "TV, audio, fireplace and lighting invisibly resolved." },
              { t: "Considered Investment", d: "Transparent quotes with a clear, defensible line-by-line breakdown." },
              { t: "Studio Coordination", d: "Direct coordination with your architect, builder or interior designer." },
              { t: "Sunshine Coast", d: "Locally sourced, locally installed, locally guaranteed." },
            ].map((c) => (
              <div key={c.t} className="bg-card p-8">
                <div className="font-serif text-2xl mb-2">{c.t}</div>
                <div className="text-sm text-muted-foreground">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="bg-[color:hsl(var(--background-alt))] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="overline">02 · The Materials</div>
          <h2 className="font-serif font-light mt-4 max-w-3xl" style={{ fontSize: "48px", lineHeight: 1.05 }}>
            Warm timber, deep stone, and considered light.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-12 border border-border">
            <img src={TEX_WOOD} className="aspect-[3/4] object-cover w-full" alt=""/>
            <img src={HERO_IMG} className="aspect-[3/4] object-cover w-full" alt=""/>
            <img src={TEX_MARBLE} className="aspect-[3/4] object-cover w-full" alt=""/>
          </div>
          <div className="mt-10 flex justify-end">
            <Link to="/designs" data-testid="btn-view-designs" className="btn-outline">
              View design gallery <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground text-background py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="overline" style={{ color: "#A68A64" }}>03 · Begin</div>
          <h2 className="font-serif font-light mt-4" style={{ fontSize: "56px", lineHeight: 1.05 }}>
            Ready to start <em className="not-italic italic" style={{ color: "#A68A64" }}>designing?</em>
          </h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">
            Tell us about your home, your architecture, your inspiration. We'll respond within one business day with the next step.
          </p>
          <Link to="/enquiry" data-testid="btn-cta-start" className="btn-accent mt-10 inline-flex" style={{ padding: "16px 28px" }}>
            Request Design Consultation <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
          </Link>
        </div>
      </section>

      <PublicFooter/>
    </div>
  );
}
